<script setup lang="ts">
import { onErrorCaptured, ref, watch } from 'vue';

import { i18n } from '@/lang';

const { resetKey } = defineProps<{ resetKey?: unknown }>();
const emit = defineEmits<{ error: [error: unknown, info: string] }>();

const t = i18n.global.t;
const error = ref<unknown>(null);

// Catch render / lifecycle / watcher throws from the slotted subtree, swap in
// the fallback, and hand the error to the mount site. shared/ must not depend
// on platform/telemetry, so reporting is the emit consumer's job, not ours.
// Returning false contains the error so it doesn't also reach
// app.config.errorHandler and get reported twice.
onErrorCaptured((err, _instance, info) => {
  error.value = err;
  emit('error', err, info);
  return false;
});

function reset(): void {
  error.value = null;
}

function reload(): void {
  window.location.reload();
}

// A resetKey change (e.g. route navigation) clears a stuck error so the next
// view renders instead of the fallback. The healthy subtree isn't remounted,
// so component reuse across param changes survives.
watch(
  () => resetKey,
  () => {
    if (error.value !== null) error.value = null;
  },
);
</script>

<template>
  <slot v-if="error === null" />
  <slot v-else name="fallback" :error="error" :reset="reset">
    <div
      data-testid="error-boundary-fallback"
      class="text-fg mx-auto flex h-full w-200 max-w-full flex-col items-center justify-center text-center"
    >
      <div class="my-4 text-lg font-semibold">{{ t('errPage.crash.title') }}</div>
      <div class="text-fg-2 mb-6 text-[15px] leading-5">{{ t('errPage.crash.info') }}</div>
      <div class="flex gap-3">
        <ElButton data-testid="error-boundary-retry" @click="reset">
          {{ t('common.retry') }}
        </ElButton>
        <ElButton type="primary" data-testid="error-boundary-reload" @click="reload">
          {{ t('common.reload') }}
        </ElButton>
      </div>
    </div>
  </slot>
</template>
